/**
 * Minimal inline sparkline — a trend cue, not a data display: it is
 * `aria-hidden` because the number it trends is always rendered next to it.
 * It deliberately renders nothing below two points rather than drawing a
 * single dot that would imply a flat trend.
 */

interface SparklineProps {
  /** Oldest first; the last value is marked as the current point. */
  values: number[];
  color: string;
  width?: number;
  height?: number;
}

const LINE_COLOR = '#A9B2BD';

const Sparkline = ({ values, color, width = 96, height = 28 }: SparklineProps) => {
  if (values.length < 2) return null;

  const pad = 4;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const x = (i: number) => pad + (i / (values.length - 1)) * (width - pad * 2);
  const y = (v: number) => height - pad - ((v - min) / span) * (height - pad * 2);
  const points = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const last = values[values.length - 1];

  return (
    <svg width={width} height={height} aria-hidden="true">
      <polyline
        points={points}
        fill="none"
        stroke={LINE_COLOR}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {/* current point: series color with a surface ring so it reads on the line */}
      <circle cx={x(values.length - 1)} cy={y(last)} r={4} fill={color} stroke="#FFFFFF" strokeWidth={2} />
    </svg>
  );
};

export default Sparkline;
